import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { PreloadedQuery, usePreloadedQuery } from "react-relay"
import { useNavigate } from "react-router"
import { TableVirtuoso } from "react-virtuoso"
import { HighlightedText } from "@/components/highlighted-text"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { searchByKeyword } from "@/lib/search-by-keyword"
import { selectContentForLanguage } from "@/lib/select-content-for-language"
import { cn } from "@/lib/utils"
import { TreatmentEmpty } from "@/modules/admin/treatment/components/patient-table-empty"
import { TreatmentTableActions } from "@/modules/admin/treatment/components/treatment-table-action"
import { TreatmentTableCountries } from "@/modules/admin/treatment/components/treatment-table-countries"
import { useTreatmentQuery } from "@/modules/admin/treatment/services/__generated__/useTreatmentQuery.graphql"
import { TreatmentPlanItem, treatmentQuery } from "@/modules/admin/treatment/services/useTreatmentQuery"

interface TreatmentTableProps {
  queryRef: PreloadedQuery<useTreatmentQuery, Record<string, unknown>>
  keyword: string
}

type ColumnProps<TreatmentPlanItem> = {
  header: string
  size?: number
  cell: (props: { data: TreatmentPlanItem; keyword: string }) => React.ReactNode
}

const columns: ColumnProps<TreatmentPlanItem>[] = [
  {
    header: "admin.treatment.treatmentTable.name",
    cell: ({ data, keyword }) => {
      const value = data.name
      return <HighlightedText text={value} searchValue={keyword} title={value} />
    },
  },
  {
    header: "admin.treatment.treatmentTable.title",
    cell: ({ data, keyword }) => {
      const value = data.title
      return <HighlightedText text={value} searchValue={keyword} title={value} />
    },
  },
  {
    header: "admin.treatment.treatmentTable.synonyms",
    cell: ({ data, keyword }) => {
      const value = data.synonym
      return <HighlightedText text={value} searchValue={keyword} title={value} />
    },
  },
  {
    header: "admin.treatment.treatmentTable.countries",
    size: 100,
    cell: ({ data }) => <TreatmentTableCountries row={data} />,
  },
  {
    header: "admin.treatment.treatmentTable.actions",
    size: 100,
    cell: ({ data }) => <TreatmentTableActions row={data} />,
  },
]

export const TreatmentTable = ({ queryRef, keyword }: TreatmentTableProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentLanguage = useCurrentLanguage()
  const [isAtTop, setIsAtTop] = useState(true)

  const { allMyTreatmentPlanTemplates } = usePreloadedQuery<useTreatmentQuery>(treatmentQuery, queryRef)

  const formattedData: TreatmentPlanItem[] = useMemo(() => {
    return allMyTreatmentPlanTemplates.map((item) => {
      const templateLanguagesWithIsoCode = item.languages.map((templateLanguage) => ({
        isoCode: convertLanguageToISOCode(templateLanguage.language).toLowerCase(),
        title: templateLanguage.title,
        synonym: templateLanguage.synonym,
      }))
      const titleByLanguage = Object.fromEntries(
        templateLanguagesWithIsoCode.map(({ isoCode, title }) => [isoCode, title]),
      )
      const synonymByLanguage = Object.fromEntries(
        templateLanguagesWithIsoCode.map(({ isoCode, synonym }) => [isoCode, synonym]),
      )
      return {
        id: item.id,
        name: item.name,
        title: selectContentForLanguage(currentLanguage, titleByLanguage),
        synonym: selectContentForLanguage(currentLanguage, synonymByLanguage),
        countries: item.countries,
      }
    })
  }, [allMyTreatmentPlanTemplates, currentLanguage])

  const filteredData = useMemo(() => {
    const trimmedKeyword = keyword.trim()

    return formattedData.filter(
      (item) =>
        searchByKeyword(item.name, trimmedKeyword) ||
        searchByKeyword(item.title, trimmedKeyword) ||
        searchByKeyword(item.synonym, trimmedKeyword),
    )
  }, [formattedData, keyword])

  return filteredData.length ? (
    <TableVirtuoso
      className="virtualized-table"
      totalCount={filteredData.length}
      atTopStateChange={setIsAtTop}
      components={{
        Table: ({ ...props }) => (
          <table {...props} className="w-full table-fixed border-separate border-spacing-0 pb-3">
            {props.children}
          </table>
        ),
        TableHead: (props) => (
          <thead
            {...props}
            className={cn(
              "text-on-surface-subdued px-4 py-2 text-xs whitespace-nowrap",
              "after:bg-background-subdued after:absolute after:top-0 after:left-0 after:-z-10 after:h-full after:w-full",
              !isAtTop && "table-header-shadow",
            )}
          />
        ),
        TableRow: (tableRowProps) => {
          const index = tableRowProps["data-index"]
          const item = filteredData[index]
          const rowKey = item.id

          return (
            <tr
              {...tableRowProps}
              data-testid="treatment-template-list-item"
              className="group bg-surface text-on-surface relative cursor-pointer border-b text-sm"
              onClick={() => navigate(`edit/${item.id}`)}>
              {columns.map((column) => (
                <td
                  key={`${rowKey}-${column.header}`}
                  className={cn(
                    "group-hover:bg-surface-hovered truncate border-b px-2 transition-all duration-300 last-of-type:w-25",
                    "first-of-type:border-l first-of-type:pl-4 first-of-type:group-last-of-type:rounded-bl-xl last-of-type:border-r last-of-type:group-last-of-type:rounded-br-xl",
                  )}>
                  {column.cell({ data: item, keyword })}
                </td>
              ))}
            </tr>
          )
        },
      }}
      fixedHeaderContent={() => (
        <tr className="bg-surface">
          {columns.map((column) => (
            <th
              key={column.header}
              style={{ width: column.size ?? "auto" }}
              className={cn(
                "border-border border-t border-b px-2 py-2.5 text-left font-normal",
                "first-of-type:rounded-tl-xl first-of-type:border-l first-of-type:pl-4 last-of-type:rounded-tr-xl last-of-type:border-r",
              )}>
              <p>{t(column.header)}</p>
            </th>
          ))}
        </tr>
      )}
    />
  ) : (
    <TreatmentEmpty title={t("common.dataNotFound")} />
  )
}
